import React, { ReactElement, useState, useEffect } from 'react';
import { FieldError } from 'react-hook-form';
import isFieldError from '../../../../helpers/isFieldError';
import styles from './selectField.module.scss';

export type Variant = 'tiny' | 'default' | 'clean';
export interface SelectFieldProps {
  select: React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >;
  label?: string | ReactElement;
  error?: string | FieldError;
  children: React.ReactNode;
  placeholder?: string;
  strict?: boolean;
  variant?: Variant;
  /** Prevents changes without native disabled, so RHF still submits the value. */
  locked?: boolean;
}

const stylePicker = (variant: Variant): string => {
  switch (variant) {
    case 'tiny':
      return styles.isTiny;
    case 'default':
    case 'clean':
      return '';
  }
};

/** Drives placeholder vs "filled" styling; `strict` treats 0 as a real choice (e.g. Sí/No). */
function isSelectFilled(
  raw: string | number | readonly string[] | undefined,
  strict: boolean,
): boolean {
  if (raw === undefined || raw === '') {
    return false;
  }
  if (strict && (raw === 0 || raw === '0')) {
    return true;
  }
  return Boolean(raw);
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  error,
  select,
  children,
  placeholder,
  strict = false,
  variant = 'default',
  locked = false,
}) => {
  const effectiveValue =
    select.value !== undefined ? select.value : select.defaultValue;

  const [hasValue, setHasValue] = useState(() =>
    isSelectFilled(effectiveValue, strict),
  );

  useEffect(() => {
    if (select.value === undefined) {
      return;
    }
    setHasValue(isSelectFilled(select.value, strict));
  }, [select.value, strict]);

  const id = select.id || select.name;
  const { onChange: selectOnChange, ...selectProps } = select;
  const errorMessage: string | JSX.Element = isFieldError(error)
    ? error.message || ''
    : error || '';
  return (
    <div className={`${styles.row}`}>
      <label className={`${styles.label} ${stylePicker(variant)}`} htmlFor={id}>
        {label}
      </label>
      <select
        data-testid={id}
        className={
          variant !== 'clean'
            ? `${styles.selectDropDown} 
        ${hasValue || !placeholder ? styles.hasValue : ''} ${stylePicker(
          variant,
        )} ${locked ? styles.isLocked : ''}`
            : locked
              ? styles.isLocked
              : ''
        }
        id={id}
        {...selectProps}
        onChange={(e) => {
          setHasValue(isSelectFilled(e.target.value, strict));
          selectOnChange?.(e);
        }}
        aria-disabled={locked || select.disabled || undefined}
        tabIndex={locked ? -1 : select.tabIndex}
        onMouseDown={(e) => {
          if (locked) {
            e.preventDefault();
            return;
          }
          select.onMouseDown?.(e);
        }}
        onKeyDown={(e) => {
          if (locked) {
            e.preventDefault();
            return;
          }
          select.onKeyDown?.(e);
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </select>
      {error && <p className={styles.errorText}>{errorMessage}</p>}
    </div>
  );
};

export default SelectField;
